"use client";

import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";

import type {
  ModalView,
  NutrafolAcademyCourse,
  NutrafolAcademyQuizOption,
} from "@/types/nutrafolAcademy";
import { MODAL_VIEWS } from "@/types/nutrafolAcademy";
import { GA4 } from "@/utils/ga4Utils";
import { segmentEvent } from "@/utils/segmentfunctions";

const LS = {
  completedSteps: "learning.completedSteps",
  currentStep: "learning.currentStep",
  quizSelections: "learning.quizSelections",
  videoProgress: "learning.videoProgress",
} as const;

const SEGMENT_VIEW_NAMES: Record<string, (stepIndex: number) => string> = {
  [MODAL_VIEWS.QUIZ]: () => "Which Nutraceutical are you taking?",
  [MODAL_VIEWS.VIDEO]: (stepIndex) => `Course ${stepIndex + 1}`,
  [MODAL_VIEWS.DONE]: () => "Hair Certified",
};

const getSegmentName = (view: ModalView, stepIndex?: number) => {
  const entry = SEGMENT_VIEW_NAMES[view];
  return entry ? entry(stepIndex ?? 0) : `Course ${(stepIndex ?? 0) + 1}`;
};

export interface LearningContextValue {
  courses: NutrafolAcademyCourse[];
  MODAL_VIEWS: typeof MODAL_VIEWS;
  currentStep: number;
  completedSteps: number[];
  quizSelections: Record<number, NutrafolAcademyQuizOption | null>;
  videoProgress: Record<number, number>;
  isModalOpen: boolean;
  modalView: ModalView;
  hydrated: boolean;
  isCompleted: (i: number) => boolean;
  isLocked: (i: number) => boolean;
  isLastStep: (i: number) => boolean;
  allDone: boolean;
  openStep: (i: number) => void;
  closeModal: () => void;
  setModalView: React.Dispatch<React.SetStateAction<ModalView>>;
  setQuizSelection: (i: number, selection: NutrafolAcademyQuizOption | null) => void;
  setVideoTime: (i: number, seconds: number) => void;
  handleQuizComplete: (selection: NutrafolAcademyQuizOption) => void;
  switchProduct: (i?: number) => void;
  handleVideoEnded: () => void;
  handleEmailSubmit: () => void;
  markStepComplete: (i: number) => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  skipToStep: (e: React.MouseEvent, targetIndex: number) => void;
}

const LearningContext = createContext<LearningContextValue | null>(null);

interface LearningProviderProps {
  courses: NutrafolAcademyCourse[];
  children: ReactNode;
}

export function LearningProvider({ courses, children }: LearningProviderProps) {
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [quizSelections, setQuizSelections] = useState<
    Record<number, NutrafolAcademyQuizOption | null>
  >({});
  const [videoProgress, setVideoProgress] = useState<Record<number, number>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalView, setModalView] = useState<ModalView>(MODAL_VIEWS.QUIZ);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const completedStorage = window.localStorage.getItem(LS.completedSteps);
      if (completedStorage) setCompletedSteps(JSON.parse(completedStorage));

      const currentStepStorage = window.localStorage.getItem(LS.currentStep);
      if (currentStepStorage) setCurrentStep(JSON.parse(currentStepStorage));

      const quizStorage = window.localStorage.getItem(LS.quizSelections);
      if (quizStorage) setQuizSelections(JSON.parse(quizStorage));

      const videoProgressStorage = window.localStorage.getItem(LS.videoProgress);
      if (videoProgressStorage) setVideoProgress(JSON.parse(videoProgressStorage));
    } catch {}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(LS.completedSteps, JSON.stringify(completedSteps));
  }, [hydrated, completedSteps]);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(LS.quizSelections, JSON.stringify(quizSelections));
  }, [hydrated, quizSelections]);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(LS.videoProgress, JSON.stringify(videoProgress));
  }, [hydrated, videoProgress]);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(LS.currentStep, JSON.stringify(currentStep));
  }, [hydrated, currentStep]);

  const needsQuiz = (i: number) => Boolean(courses[i]?.quiz) && !quizSelections[i];
  const isCompleted = (i: number) => completedSteps.includes(i);
  const isLocked = (i: number) => i > 0 && !isCompleted(i - 1);
  const isLastStep = (i: number) => i === courses.length - 1;
  const allDone = completedSteps.length === courses.length;

  const openStep = (i: number) => {
    if (isLocked(i)) return;

    GA4.onModalViewed({ name: getSegmentName(MODAL_VIEWS.VIDEO, i) });

    setCurrentStep(i);
    setModalView(needsQuiz(i) ? MODAL_VIEWS.QUIZ : MODAL_VIEWS.VIDEO);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    segmentEvent("Modal Closed", {
      name: getSegmentName(modalView, currentStep),
    });

    setIsModalOpen(false);
  };

  const markStepComplete = (i: number) => {
    setCompletedSteps((prev) => (prev.includes(i) ? prev : [...prev, i]));
  };

  const setQuizSelection = (i: number, selection: NutrafolAcademyQuizOption | null) => {
    setQuizSelections((prev) => ({ ...prev, [i]: selection }));
    setVideoProgress((prev) => ({ ...prev, [i]: 0 }));
  };

  const setVideoTime = (i: number, seconds: number) => {
    setVideoProgress((prev) => (prev[i] === seconds ? prev : { ...prev, [i]: seconds }));
  };

  const handleQuizComplete = (selection: NutrafolAcademyQuizOption) => {
    segmentEvent("Modal Tab Viewed", {
      text: selection?.productName,
    });
    setQuizSelection(currentStep, selection);
    setModalView(MODAL_VIEWS.VIDEO);
  };

  const switchProduct = (i = currentStep) => {
    GA4.onMarketingCtaClicked({
      destination: "/nutrafolacademy/",
      module_location: "mid_fold",
      text: "Switch Nutraceutical",
      type: "link",
    });

    setQuizSelections((prev) => ({ ...prev, [i]: null }));
    setVideoProgress((prev) => ({ ...prev, [i]: 0 }));
    setModalView(MODAL_VIEWS.QUIZ);
  };

  const handleVideoEnded = () => {
    const i = currentStep;
    markStepComplete(i);
    setVideoTime(i, 0);

    const finishedCount = new Set([...completedSteps, i]).size;
    const isLast = i === courses.length - 1;

    if (isLast && finishedCount === courses.length) {
      GA4.onModalViewed({ name: getSegmentName(MODAL_VIEWS.EMAIL) });

      setModalView(MODAL_VIEWS.EMAIL);
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  };

  const handleEmailSubmit = () => {
    GA4.onModalViewed({ name: getSegmentName(MODAL_VIEWS.DONE) });
    setModalView(MODAL_VIEWS.DONE);
  };

  const skipToStep = (e: React.MouseEvent, targetIndex: number) => {
    GA4.onMarketingCtaClicked({
      text: (e?.target as HTMLElement)?.innerText,
      destination: "/nutrafolacademy/",
      module_location: "mid_fold",
      type: "link",
    });

    setCompletedSteps((prev) => {
      const s = new Set(prev);
      for (let j = 0; j < targetIndex; j++) s.add(j);
      return Array.from(s).sort((a, b) => a - b);
    });

    openStep(targetIndex);
  };

  const value: LearningContextValue = {
    courses,
    MODAL_VIEWS,
    currentStep,
    completedSteps,
    quizSelections,
    videoProgress,
    isModalOpen,
    modalView,
    hydrated,
    isCompleted,
    isLocked,
    isLastStep,
    allDone,
    openStep,
    closeModal,
    setModalView,
    setQuizSelection,
    setVideoTime,
    handleQuizComplete,
    switchProduct,
    handleVideoEnded,
    handleEmailSubmit,
    markStepComplete,
    setCurrentStep,
    skipToStep,
  };

  return <LearningContext.Provider value={value}>{children}</LearningContext.Provider>;
}

export const useLearning = () => {
  const context = useContext(LearningContext);
  if (!context) {
    throw new Error("useLearning must be used within a LearningProvider");
  }
  return context;
};
